import * as Sentry from '@sentry/react'
import axios, { AxiosError } from 'axios'

import { clearAuthTokensSync, getAuthTokens, setAuthTokensSync } from '@/lib/native/authTokens'

import environment from '../environment'
import { getImpersonatedUserId, IMPERSONATE_USER_ID_HEADER, shouldAttachImpersonationHeader } from '../lib/impersonation'

const defaultOptions = {
  baseURL: environment.apiBaseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
  maxContentLength: 50 * 1024 * 1024, // 50 MB
  maxBodyLength: 50 * 1024 * 1024, // 50 MB
}

const instance = axios.create(defaultOptions)

let isRefreshing = false
let failedRequestsQueue: { resolve: (token: string) => void; reject: (error: unknown) => void }[] = []

//doing the requests that failed due to token expiration with the new token
const processQueue = (error: unknown, token: string | null = null) => {
  failedRequestsQueue.forEach(prom => {
    if (token) {
      prom.resolve(token)
    } else {
      prom.reject(error)
    }
  })
  failedRequestsQueue = []
}

const isBrowserOffline = () => typeof navigator !== 'undefined' && navigator.onLine === false

const reportApiErrorToSentry = (error: AxiosError) => {
  const status = error.response?.status
  const isNetworkError = !error.response
  // Offline tabs, 4xx, and expected client errors are not Sentry-worthy.
  if (isNetworkError && isBrowserOffline()) {
    return
  }
  if (!isNetworkError && !(status && status >= 500)) {
    return
  }

  const responseData = error.response?.data as { sentryEventId?: string; traceId?: string } | undefined

  Sentry.withScope(scope => {
    scope.setExtra('url', error.config?.url)
    scope.setExtra('method', error.config?.method)
    scope.setExtra('status', status)
    scope.setExtra('baseURL', error.config?.baseURL)
    if (responseData?.sentryEventId) {
      scope.setExtra('serverSentryEventId', responseData.sentryEventId)
    }
    if (responseData?.traceId) {
      scope.setExtra('serverTraceId', responseData.traceId)
    }
    Sentry.captureException(error)
  })
}

//adding token to requests
instance.interceptors.request.use(config => {
  const traceData = Sentry.getTraceData()
  if (traceData['sentry-trace']) {
    config.headers!['sentry-trace'] = traceData['sentry-trace']
  }
  if (traceData.baggage) {
    config.headers!.baggage = traceData.baggage
  }

  if (config.baseURL?.includes('auth') || config.baseURL?.includes('approveTransaction')) {
    return config
  }
  const tokens = getAuthTokens()
  if (tokens?.accessToken) {
    config.headers!.Authorization = `Bearer ${tokens.accessToken}`
  }

  const impersonatedUserId = getImpersonatedUserId()
  const requestUrl = config.url ?? ''
  if (impersonatedUserId && shouldAttachImpersonationHeader(requestUrl)) {
    config.headers![IMPERSONATE_USER_ID_HEADER] = impersonatedUserId
  }

  return config
})

//handling token expiration if there was error response
instance.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config
    if (!originalRequest._retry && error.response && (error.response.status === 401 || error.response.status === 403)) {
      const tokens = getAuthTokens()
      if (!tokens?.refreshToken) {
        clearAuthTokensSync()
        window.location.reload()
        return Promise.reject(error)
      }
      //if we had error while we refresh we add it to queue so we wont have concurrent requests
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedRequestsQueue.push({
            resolve: token => {
              originalRequest.headers.Authorization = `Bearer ${token}`
              resolve(axios(originalRequest))
            },
            reject: err => {
              reject(err)
            },
          })
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const { data } = await axios.post(`${environment.apiBaseUrl}/auth/refresh-token`, {
          refreshToken: tokens.refreshToken,
        })

        setAuthTokensSync({ ...tokens, accessToken: data.accessToken })

        processQueue(null, data.accessToken)
        isRefreshing = false

        originalRequest.headers.Authorization = `Bearer ${data.accessToken}`
        return axios(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError, null)
        clearAuthTokensSync()
        window.location.reload()
        return Promise.reject(refreshError)
      }
    }
    if (axios.isAxiosError(error)) {
      reportApiErrorToSentry(error)
    }
    return Promise.reject(error)
  }
)
export const getErrorMessage = (error: unknown, defaultMessage?: string) => {
  if (!error) return defaultMessage ?? 'Unknown error'
  return (
    (error as ErrorResponse).response?.data?.msg ||
    (error as ErrorResponse).response?.data?.error ||
    (error as ErrorResponse).response?.data?.message ||
    defaultMessage ||
    (error as Error).message ||
    JSON.stringify(error)
  )
}

export const getApiErrorDetails = (error: unknown): Record<string, unknown> | undefined => {
  return (error as ErrorResponse).response?.data?.details
}
export type ApiResponse<T> = {
  success: boolean
  message: string
  data: T
  response?: T
  msg?: string
  error?: string
}
export default instance
export type ErrorResponse = AxiosError<{
  msg?: string
  error?: string
  message?: string
  details?: Record<string, unknown>
  sentryEventId?: string
  traceId?: string
}>
